import { useEffect, useId, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { Modal, ModalSubtitle, ModalTitle } from "@/components/Modal";
import { Select } from "@/components/Select";
import { TextInput } from "@/components/TextInput";
import type { ToastVariant } from "@/components/Toast";
import { useApi } from "@/lib/api";
import type { PagedResult } from "@/lib/pagination";
import { RESOURCE_TYPE_LABELS } from "@/lib/resourceTypeLabels";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useResourceTags } from "@/lib/useResourceTags";
import { ANY_TYPE, type AccessRule } from "./shared";

const TAG_SAMPLE_SIZE = 100;

interface TaggedResource {
  id: string;
  name: string;
  tags: { key: string; value: string }[];
}

export function DynamicRuleModal({
  rule,
  teamName,
  showToast,
  onClose,
  onSaved,
}: {
  rule: AccessRule | null;
  teamName: string;
  showToast: (message: string, variant: ToastVariant) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId, teamId } = useParams();
  const keyListId = useId();
  const valueListId = useId();
  const [name, setName] = useState(rule?.name ?? "");
  const [type, setType] = useState(rule ? (rule.resourceType ?? ANY_TYPE) : "");
  const [tagKey, setTagKey] = useState(rule?.tagKey ?? "");
  const debouncedTagKey = useDebouncedValue(tagKey, 300);
  const [tagValue, setTagValue] = useState(rule?.tagValue ?? "");
  const debouncedTagValue = useDebouncedValue(tagValue, 300);
  const [preview, setPreview] = useState<{
    key: string;
    value: string;
    type: string;
    count: number;
  } | null>(null);
  const [tagged, setTagged] = useState<TaggedResource[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { tagKeys, tagValuesFor } = useResourceTags();

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void api
      .get<PagedResult<TaggedResource>>(
        `/organizations/${orgId}/resources?page=1&pageSize=${TAG_SAMPLE_SIZE}`,
      )
      .then((data) => {
        if (!cancelled) {
          setTagged(data?.items ?? []);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId]);

  useEffect(() => {
    if (
      !accessToken ||
      !orgId ||
      !teamId ||
      !debouncedTagKey ||
      !debouncedTagValue
    ) {
      return;
    }

    let cancelled = false;

    async function loadPreviewCount() {
      const params = new URLSearchParams({
        tagKey: debouncedTagKey,
        tagValue: debouncedTagValue,
      });

      if (type && type !== ANY_TYPE) {
        params.set("resourceType", type);
      }

      const data = await api.get<{ resourceCount: number }>(
        `/organizations/${orgId}/teams/${teamId}/access-rules/preview?${params.toString()}`,
      );

      if (!cancelled && data) {
        setPreview({
          key: debouncedTagKey,
          value: debouncedTagValue,
          type,
          count: data.resourceCount,
        });
      }
    }

    void loadPreviewCount();

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    api,
    orgId,
    teamId,
    type,
    debouncedTagKey,
    debouncedTagValue,
  ]);

  const matchNames = useMemo(
    () =>
      tagged
        .filter((r) =>
          r.tags.some((t) => t.key === tagKey && t.value === tagValue),
        )
        .map((r) => r.name),
    [tagged, tagKey, tagValue],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId || !teamId) {
      return;
    }

    setSubmitting(true);

    try {
      const body = {
        name,
        resourceType: type === ANY_TYPE ? null : type,
        tagKey,
        tagValue,
      };

      if (rule) {
        await api.send(
          `/organizations/${orgId}/teams/${teamId}/access-rules/${rule.id}`,
          "PATCH",
          body,
          "Couldn't update rule.",
        );
        showToast(`${name} updated.`, "success");
      } else {
        await api.send(
          `/organizations/${orgId}/teams/${teamId}/access-rules`,
          "POST",
          { ...body, kind: "Dynamic" },
          "Couldn't create rule.",
        );
        showToast(`${name} added to ${teamName}.`, "success");
      }

      onSaved();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't save rule.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const hasTag = Boolean(tagKey && tagValue);
  const isCurrent =
    preview !== null &&
    preview.key === debouncedTagKey &&
    preview.value === debouncedTagValue &&
    preview.type === type;

  return (
    <Modal onClose={onClose} scrollable={false}>
      <div className="mb-4 shrink-0">
        <ModalTitle>
          {rule ? "Edit dynamic rule" : "Add dynamic rule"}
        </ModalTitle>
        <ModalSubtitle className="mt-1">
          Grant access by tag so resources are selected automatically.
        </ModalSubtitle>
      </div>
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div>
            <label
              htmlFor="dynamicRuleName"
              className="mb-1.5 block text-sm text-foreground"
            >
              Name
            </label>
            <TextInput
              type="text"
              id="dynamicRuleName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              className="w-full"
            />
          </div>

          <fieldset>
            <legend className="mb-1.5 text-sm text-foreground">Rule</legend>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-muted bg-base p-3 text-sm text-foreground/70">
              <Select
                aria-label="Resource type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                required
                className="h-8"
              >
                <option value="" disabled>
                  Select a type...
                </option>
                <option value={ANY_TYPE}>Any type</option>
                {Object.entries(RESOURCE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <span>where</span>
              <TextInput
                type="text"
                aria-label="Tag key"
                list={keyListId}
                value={tagKey}
                onChange={(e) => setTagKey(e.target.value)}
                placeholder="Environment"
                required
                className="h-8 w-36"
              />
              <datalist id={keyListId}>
                {tagKeys.map((key) => (
                  <option key={key} value={key} />
                ))}
              </datalist>
              <span>=</span>
              <TextInput
                type="text"
                aria-label="Tag value"
                list={valueListId}
                value={tagValue}
                onChange={(e) => setTagValue(e.target.value)}
                placeholder="Production"
                required
                className="h-8 w-36"
              />
              <datalist id={valueListId}>
                {tagValuesFor(tagKey).map((value) => (
                  <option key={value} value={value} />
                ))}
              </datalist>
            </div>
            <p className="mt-1.5 text-xs text-foreground/70">
              Suggestions come from tags already in use.
            </p>
          </fieldset>

          {!hasTag ? (
            <div className="rounded-lg border border-muted bg-surface p-4">
              <p className="text-sm text-foreground/70">
                Enter a tag key and value to see how many resources this rule
                would match.
              </p>
            </div>
          ) : !isCurrent ? (
            <div className="rounded-lg border border-muted bg-surface p-4">
              <p className="text-sm text-foreground/70">
                Matching resources...
              </p>
            </div>
          ) : preview.count === 0 ? (
            <div className="rounded-lg border border-info/35 bg-info/10 p-4">
              <p className="text-sm font-medium text-info">
                No matching resources yet
              </p>
              <p className="mt-1 text-sm text-foreground/70">
                Resources that match this key-value pair in the future will be
                included automatically.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-success/35 bg-success/10 p-4">
              <p className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tabular-nums text-success">
                  {preview.count}
                </span>
                <span className="text-sm text-foreground">
                  {preview.count === 1 ? "resource matches" : "resources match"}
                </span>
              </p>
              {matchNames.length > 0 && (
                <p className="mt-1.5 text-xs text-foreground/70">
                  {matchNames.slice(0, 4).join(", ")}
                  {preview.count > matchNames.slice(0, 4).length &&
                    ` and ${preview.count - matchNames.slice(0, 4).length} more`}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex shrink-0 justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : rule ? "Save" : "Add rule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
